/**
 * Condor Binary Polar Parser
 * Parses Condor's native binary .pol files (cubic Bezier curves),
 * .pdt metadata files, and plane-info .txt files.
 *
 * Ported from xc-polar-comparison/server/condorDataService.js
 */

function roundTo(value, decimals = 2) {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

function parsePositiveNumber(value) {
  return Number.isFinite(value) && value > 0 ? value : null;
}

function normalizeSinkValue(rawSinkMs) {
  if (!Number.isFinite(rawSinkMs)) return null;
  return rawSinkMs <= 0 ? rawSinkMs : -Math.abs(rawSinkMs);
}

function dedupePolarPoints(points) {
  if (!Array.isArray(points)) return [];

  const sorted = points
    .filter((p) => p && Number.isFinite(p.speedKmh) && p.speedKmh > 0 && Number.isFinite(p.sinkMs))
    .sort((a, b) => a.speedKmh - b.speedKmh);

  const deduped = [];
  sorted.forEach((point) => {
    const prev = deduped[deduped.length - 1];
    if (prev && Math.abs(prev.speedKmh - point.speedKmh) < 1e-4) {
      prev.sinkMs = (prev.sinkMs + point.sinkMs) / 2;
      return;
    }
    deduped.push({ speedKmh: point.speedKmh, sinkMs: point.sinkMs });
  });

  return deduped;
}

function cubicBezierValue(p0, p1, p2, p3, t) {
  const oneMinusT = 1 - t;
  return (
    (oneMinusT ** 3) * p0
    + (3 * (oneMinusT ** 2) * t * p1)
    + (3 * oneMinusT * (t ** 2) * p2)
    + ((t ** 3) * p3)
  );
}

function buildCubicBezierPolarPoints(records) {
  if (!Array.isArray(records) || records.length < 2) return [];

  const points = [];
  const pushPoint = (speedKmh, sinkMs) => {
    if (!Number.isFinite(speedKmh) || speedKmh <= 0 || !Number.isFinite(sinkMs)) return;
    points.push({ speedKmh, sinkMs: normalizeSinkValue(sinkMs) });
  };

  for (let i = 0; i < records.length - 1; i += 1) {
    const current = records[i];
    const next = records[i + 1];
    if (!current || !next) continue;

    const p0 = current.a;
    const p1 = current.c;
    const p2 = next.b;
    const p3 = next.a;
    if (
      !p0 || !p1 || !p2 || !p3
      || !Number.isFinite(p0.speedKmh) || !Number.isFinite(p0.sinkMs)
      || !Number.isFinite(p1.speedKmh) || !Number.isFinite(p1.sinkMs)
      || !Number.isFinite(p2.speedKmh) || !Number.isFinite(p2.sinkMs)
      || !Number.isFinite(p3.speedKmh) || !Number.isFinite(p3.sinkMs)
    ) continue;

    const segmentSpan = Math.abs(p3.speedKmh - p0.speedKmh);
    const segmentSamples = Math.max(20, Math.min(220, Math.round(segmentSpan / 0.45)));

    for (let sampleIdx = 0; sampleIdx <= segmentSamples; sampleIdx += 1) {
      if (i > 0 && sampleIdx === 0) continue;

      const t = sampleIdx / segmentSamples;
      const speedKmh = cubicBezierValue(p0.speedKmh, p1.speedKmh, p2.speedKmh, p3.speedKmh, t);
      const sinkMs = cubicBezierValue(p0.sinkMs, p1.sinkMs, p2.sinkMs, p3.sinkMs, t);
      pushPoint(speedKmh, sinkMs);
    }
  }

  // Also include the anchor points themselves
  records.forEach((record) => {
    if (record?.a) pushPoint(record.a.speedKmh, record.a.sinkMs);
  });

  return dedupePolarPoints(points);
}

function createPolarFromPoints(points, metadata = {}) {
  const dedupedPoints = dedupePolarPoints(points);
  if (dedupedPoints.length < 3) return null;

  const wingArea = parsePositiveNumber(metadata.wingArea);
  const minWingLoading = parsePositiveNumber(metadata.minWingLoading);
  const maxWingLoading = parsePositiveNumber(metadata.maxWingLoading);
  const defaultWingLoading = (
    parsePositiveNumber(metadata.defaultWingLoading)
    || minWingLoading
    || parsePositiveNumber(metadata.referenceWingLoading)
    || maxWingLoading
  );
  const referenceWingLoading = (
    parsePositiveNumber(metadata.referenceWingLoading)
    || defaultWingLoading
  );
  const resolvedMinWingLoading = minWingLoading || defaultWingLoading;
  const resolvedMaxWingLoading = maxWingLoading || defaultWingLoading;
  const mass = (
    parsePositiveNumber(metadata.mass)
    || (
      Number.isFinite(referenceWingLoading) && referenceWingLoading > 0
      && Number.isFinite(wingArea) && wingArea > 0
        ? referenceWingLoading * wingArea
        : null
    )
  );

  return {
    mass,
    maxWaterBallast: parsePositiveNumber(metadata.maxWaterBallastLiters),
    polarSpeeds: dedupedPoints.map((p) => p.speedKmh),
    polarSinks: dedupedPoints.map((p) => p.sinkMs),
    wingArea,
    className: metadata.className || null,
    handicap: parsePositiveNumber(metadata.handicap),
    minWingLoading: resolvedMinWingLoading,
    defaultWingLoading,
    defaultWingLoadingSource: metadata.defaultWingLoadingSource || metadata.maxWingLoadingSource || null,
    referenceWingLoading,
    maxWingLoading: resolvedMaxWingLoading,
    maxWingLoadingSource: metadata.maxWingLoadingSource || null,
    baseMassNoFixedKg: parsePositiveNumber(metadata.baseMassNoFixedKg),
    fixedMassKg: Number.isFinite(metadata.fixedMassKg) && metadata.fixedMassKg >= 0
      ? metadata.fixedMassKg
      : null,
    fixedDefaultEnabled: Boolean(metadata.fixedDefaultEnabled),
    fuelCapacityKg: parsePositiveNumber(metadata.fuelCapacityKg) || 0,
    fuelDefaultKg: parsePositiveNumber(metadata.fuelDefaultKg) || 0,
    maxNoFixedWingLoading: parsePositiveNumber(metadata.maxNoFixedWingLoading),
    rawFormat: metadata.rawFormat || 'binary-pol',
    vneKmh: parsePositiveNumber(metadata.vneKmh) || null
  };
}

/**
 * Parse a Condor binary .pol file
 * Format: uint32 pointCount, then pointCount * 24 bytes of Bezier control points
 */
export function parseBinaryPolarFile(buffer, planeInfo = null, planePdt = null) {
  if (!(buffer instanceof ArrayBuffer) || buffer.byteLength < 4) return null;

  const view = new DataView(buffer);
  const pointCount = view.getUint32(0, true);
  const expectedSize = 4 + (pointCount * 24);
  if (pointCount < 2 || expectedSize !== buffer.byteLength) return null;

  const records = [];
  for (let i = 0; i < pointCount; i += 1) {
    const offset = 4 + (i * 24);
    records.push({
      a: {
        speedKmh: view.getFloat32(offset, true),
        sinkMs: normalizeSinkValue(view.getFloat32(offset + 4, true))
      },
      b: {
        speedKmh: view.getFloat32(offset + 8, true),
        sinkMs: normalizeSinkValue(view.getFloat32(offset + 12, true))
      },
      c: {
        speedKmh: view.getFloat32(offset + 16, true),
        sinkMs: normalizeSinkValue(view.getFloat32(offset + 20, true))
      }
    });
  }

  const points = buildCubicBezierPolarPoints(records);
  if (points.length < 3) return null;

  const wingArea = parsePositiveNumber(planePdt?.wingArea)
    || parsePositiveNumber(planeInfo?.wingArea);
  const txtDryWingLoading = parsePositiveNumber(planeInfo?.dryWingLoading)
    || parsePositiveNumber(planeInfo?.minWingLoading);
  const pdtCurveReferenceWingLoading = parsePositiveNumber(planePdt?.curveReferenceWingLoading)
    || parsePositiveNumber(planePdt?.referenceWingLoading);
  const pdtDefaultWingLoading = parsePositiveNumber(planePdt?.defaultWingLoading);
  const defaultWingLoading = pdtDefaultWingLoading || txtDryWingLoading || pdtCurveReferenceWingLoading;
  const minWingLoading = parsePositiveNumber(planeInfo?.minWingLoading) || defaultWingLoading;
  const pdtMaxWingLoading = parsePositiveNumber(planePdt?.maxWingLoading);
  const txtMaxWingLoading = parsePositiveNumber(planeInfo?.maxWingLoading);
  let maxWingLoading = null;
  let maxWingLoadingSource = null;
  if (Number.isFinite(pdtMaxWingLoading) && pdtMaxWingLoading > 0) {
    maxWingLoading = pdtMaxWingLoading;
    maxWingLoadingSource = 'plane_pdt';
  }
  if (
    Number.isFinite(txtMaxWingLoading) && txtMaxWingLoading > 0
    && (!Number.isFinite(maxWingLoading) || txtMaxWingLoading > maxWingLoading + 1e-6)
  ) {
    maxWingLoading = txtMaxWingLoading;
    maxWingLoadingSource = 'plane_txt';
  }
  const pdtReferenceMassKg = parsePositiveNumber(planePdt?.defaultReferenceMassKg)
    || parsePositiveNumber(planePdt?.referenceMassKg);
  const emptyMassKg = parsePositiveNumber(planeInfo?.emptyMassKg);
  const inferredMass = (
    Number.isFinite(pdtReferenceMassKg) && pdtReferenceMassKg > 0
  )
    ? pdtReferenceMassKg
    : (Number.isFinite(emptyMassKg) && emptyMassKg > 0)
      ? emptyMassKg
      : (
        Number.isFinite(defaultWingLoading) && defaultWingLoading > 0
        && Number.isFinite(wingArea) && wingArea > 0
          ? defaultWingLoading * wingArea
          : null
      );

  return createPolarFromPoints(points, {
    mass: inferredMass,
    wingArea,
    className: planeInfo?.className || null,
    handicap: parsePositiveNumber(planeInfo?.handicap),
    minWingLoading,
    defaultWingLoading,
    defaultWingLoadingSource: pdtDefaultWingLoading ? 'plane_pdt' : (txtDryWingLoading ? 'plane_txt' : null),
    referenceWingLoading: pdtCurveReferenceWingLoading || defaultWingLoading,
    maxWingLoading,
    maxWingLoadingSource,
    baseMassNoFixedKg: parsePositiveNumber(planePdt?.baseMassNoFixedKg),
    fixedMassKg: Number.isFinite(planePdt?.fixedMassKg) && planePdt.fixedMassKg >= 0
      ? planePdt.fixedMassKg
      : null,
    fixedDefaultEnabled: Boolean(planePdt?.fixedDefaultEnabled),
    fuelCapacityKg: parsePositiveNumber(planePdt?.fuelCapacityKg) || 0,
    fuelDefaultKg: parsePositiveNumber(planePdt?.fuelDefaultKg) || 0,
    maxWaterBallastLiters: parsePositiveNumber(planePdt?.maxBallastLiters),
    maxNoFixedWingLoading: parsePositiveNumber(planePdt?.maxNoFixedWingLoading),
    rawFormat: 'binary-pol'
  });
}

/**
 * Parse a plane-info .txt file
 */
export function parsePlaneInfoFile(content) {
  const lines = String(content || '')
    .split('\n')
    .map((line) => line.replace(/\r/g, '').trim())
    .filter((line) => line.length > 0);

  if (lines.length === 0) return null;

  const readValue = (regex) => {
    for (let i = 0; i < lines.length; i += 1) {
      const match = lines[i].match(regex);
      if (!match) continue;
      const parsed = Number.parseFloat(match[1]);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  };

  const readLastMetricValue = (linePrefix, unitRegex) => {
    for (let i = 0; i < lines.length; i += 1) {
      const line = lines[i];
      if (!line.toLowerCase().startsWith(linePrefix)) continue;
      const matches = Array.from(line.matchAll(unitRegex));
      if (matches.length === 0) return null;
      const parsed = Number.parseFloat(matches[matches.length - 1][1]);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  };

  const wingArea = readLastMetricValue('wing area,', /([+-]?\d+(?:\.\d+)?)\s*m2/gi);
  const emptyMassKg = readValue(/^Empty (?:mass|weight),\s*([+-]?\d+(?:\.\d+)?)\s*kg/i);
  const maxMassKg = readValue(/^Max (?:mass|weight),\s*([+-]?\d+(?:\.\d+)?)\s*kg/i);
  const minWingLoadingRaw = readLastMetricValue('min wing loading,', /([+-]?\d+(?:\.\d+)?)\s*kg\/m2/gi);
  const maxWingLoadingRaw = readLastMetricValue('max wing loading,', /([+-]?\d+(?:\.\d+)?)\s*kg\/m2/gi);

  const minWingLoading = Number.isFinite(minWingLoadingRaw) && minWingLoadingRaw > 0
    ? minWingLoadingRaw
    : (
      Number.isFinite(emptyMassKg) && emptyMassKg > 0
      && Number.isFinite(wingArea) && wingArea > 0
        ? emptyMassKg / wingArea
        : null
    );
  const maxWingLoading = Number.isFinite(maxWingLoadingRaw) && maxWingLoadingRaw > 0
    ? maxWingLoadingRaw
    : (
      Number.isFinite(maxMassKg) && maxMassKg > 0
      && Number.isFinite(wingArea) && wingArea > 0
        ? maxMassKg / wingArea
        : null
    );

  return {
    className: lines[0] || null,
    wingArea,
    emptyMassKg,
    maxMassKg,
    handicap: readValue(/^DAeC index,\s*([+-]?\d+(?:\.\d+)?)/i),
    maxSpeedKmh: readValue(/^Max speed,\s*([+-]?\d+(?:\.\d+)?)\s*km\/h/i),
    minWingLoading,
    maxWingLoading,
    dryWingLoading: minWingLoading
  };
}

/**
 * Parse a Condor binary .pdt file
 * Key offsets: 420=maxBallast, 444=wingArea, 448=refMassBase, 452=refMassAdj, 480=maxMass, 496=fuelCap
 */
export function parsePlanePdtFile(buffer) {
  if (!(buffer instanceof ArrayBuffer) || buffer.byteLength < 500) return null;

  const view = new DataView(buffer);
  const readFloat32Raw = (offset) => {
    if (offset < 0 || offset + 4 > view.byteLength) return null;
    const value = view.getFloat32(offset, true);
    return Number.isFinite(value) ? value : null;
  };
  const readPositiveFloat32 = (offset) => parsePositiveNumber(readFloat32Raw(offset));

  const maxBallastRaw = readFloat32Raw(420);
  const maxBallastLiters = readPositiveFloat32(420);
  const maxBallastForMassMathKg = Number.isFinite(maxBallastRaw) && maxBallastRaw >= 0
    ? maxBallastRaw
    : 0;
  const wingArea = readPositiveFloat32(444);
  const referenceMassBaseKg = readPositiveFloat32(448);
  const referenceMassAdjustmentRaw = readFloat32Raw(452);
  const referenceMassAdjustmentKg = Number.isFinite(referenceMassAdjustmentRaw)
    ? roundTo(referenceMassAdjustmentRaw, 3)
    : 0;
  const fuelCapacityKg = Math.max(0, readPositiveFloat32(496) || 0);
  const maxMassKg = readPositiveFloat32(480);

  const referenceMassDryRaw = (
    Number.isFinite(referenceMassBaseKg) && referenceMassBaseKg > 0
  )
    ? referenceMassBaseKg + (Number.isFinite(referenceMassAdjustmentRaw) ? referenceMassAdjustmentRaw : 0)
    : null;
  const referenceMassCombinedRaw = (
    Number.isFinite(referenceMassDryRaw) && referenceMassDryRaw > 0
  )
    ? referenceMassDryRaw + fuelCapacityKg
    : null;
  const referenceMassKg = (
    Number.isFinite(referenceMassCombinedRaw) && referenceMassCombinedRaw > 0
  )
    ? Math.round(referenceMassCombinedRaw)
    : null;

  const baseMassNoFixedKg = (
    Number.isFinite(referenceMassDryRaw) && referenceMassDryRaw > 0
  )
    ? referenceMassDryRaw
    : null;
  const noFixedMaxMassByTanksKg = (
    Number.isFinite(baseMassNoFixedKg) && baseMassNoFixedKg > 0
  )
    ? baseMassNoFixedKg + fuelCapacityKg + maxBallastForMassMathKg
    : null;
  const fixedMassFromGapKg = (
    Number.isFinite(maxMassKg) && maxMassKg > 0
    && Number.isFinite(noFixedMaxMassByTanksKg) && noFixedMaxMassByTanksKg > 0
    && maxMassKg > noFixedMaxMassByTanksKg + 0.5
  )
    ? roundTo(maxMassKg - noFixedMaxMassByTanksKg, 3)
    : 0;

  const curveReferenceMassKg = referenceMassBaseKg;
  const defaultReferenceMassKg = referenceMassKg;

  const curveReferenceWingLoading = (
    Number.isFinite(curveReferenceMassKg) && curveReferenceMassKg > 0
    && Number.isFinite(wingArea) && wingArea > 0
      ? curveReferenceMassKg / wingArea
      : null
  );
  const defaultWingLoading = (
    Number.isFinite(defaultReferenceMassKg) && defaultReferenceMassKg > 0
    && Number.isFinite(wingArea) && wingArea > 0
      ? defaultReferenceMassKg / wingArea
      : null
  );
  const referenceWingLoading = (
    Number.isFinite(referenceMassKg) && referenceMassKg > 0
    && Number.isFinite(wingArea) && wingArea > 0
      ? referenceMassKg / wingArea
      : null
  );
  const maxWingLoading = (
    Number.isFinite(maxMassKg) && maxMassKg > 0
    && Number.isFinite(wingArea) && wingArea > 0
      ? maxMassKg / wingArea
      : null
  );
  const maxNoFixedMassKg = (
    Number.isFinite(noFixedMaxMassByTanksKg) && noFixedMaxMassByTanksKg > 0
      ? noFixedMaxMassByTanksKg
      : maxMassKg
  );
  const maxNoFixedWingLoading = (
    Number.isFinite(maxNoFixedMassKg) && maxNoFixedMassKg > 0
    && Number.isFinite(wingArea) && wingArea > 0
      ? maxNoFixedMassKg / wingArea
      : null
  );

  if (
    !Number.isFinite(wingArea)
    && !Number.isFinite(referenceMassKg)
    && !Number.isFinite(referenceWingLoading)
    && !Number.isFinite(defaultWingLoading)
    && !Number.isFinite(maxWingLoading)
    && !Number.isFinite(maxBallastLiters)
  ) return null;

  return {
    wingArea,
    maxBallastLiters,
    curveReferenceMassKg,
    referenceMassAdjustmentKg,
    referenceMassKg,
    defaultReferenceMassKg,
    defaultWingLoading,
    curveReferenceWingLoading,
    referenceWingLoading,
    baseMassNoFixedKg,
    fixedMassKg: fixedMassFromGapKg,
    fixedDefaultEnabled: false,
    fuelCapacityKg,
    fuelDefaultKg: fuelCapacityKg,
    maxMassKg,
    maxBallastForMassMathKg,
    maxNoFixedMassKg,
    maxWingLoading,
    maxNoFixedWingLoading
  };
}

/**
 * Parse CoTASA_data.txt for VNE values
 * Format: GliderName=VNE_kmh,... (first value is VNE)
 */
export function parseCotasaVneData(content) {
  const vneMap = new Map();
  const lines = String(content || '').split('\n');

  lines.forEach((lineRaw) => {
    const line = lineRaw.trim();
    if (line.length === 0 || line.startsWith('#') || line.startsWith('[')) return;

    const eqIdx = line.indexOf('=');
    if (eqIdx <= 0) return;

    const gliderType = line.slice(0, eqIdx).trim();
    const valuesPart = line.slice(eqIdx + 1).trim();
    if (gliderType.length === 0 || valuesPart.length === 0) return;

    const firstToken = valuesPart.split(',')[0]?.trim();
    const vneKmh = Number.parseFloat(firstToken);
    if (!Number.isFinite(vneKmh) || vneKmh <= 0) return;
    vneMap.set(gliderType, vneKmh);
  });

  return vneMap;
}

/**
 * Look up VNE for a variant name, falling back to base name without -FXB suffix
 */
export function getCotasaVneForVariant(cotasaVneByType, variantName) {
  if (!(cotasaVneByType instanceof Map) || !variantName) return null;

  const direct = cotasaVneByType.get(variantName);
  if (Number.isFinite(direct) && direct > 0) return direct;

  const baseName = variantName.replace(/-FXB$/, '');
  const base = cotasaVneByType.get(baseName);
  if (Number.isFinite(base) && base > 0) return base;

  return null;
}
